/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  SFFormBuilder,
  SFPageContainer,
  SFCard,
  SFCardBody,
  SFSkeleton,
  SFGridContainer,
  SFGridContainerItem,
  SFGridItem,
  SFDivider,
  SFActionBar,
} from "@sf-core";
import { toastActions, layoutActions } from "@base-provider";
import getSchema from "./schema.js";
import uischema from "./uischema.json";
import {
  getTenant,
  addTenant,
  editTenant,
  deleteTenant,
  restoreTenant,
  syncTenantSkills,
} from "../../services/tenantService.js";
import { ACTION_TYPE } from "../../constants/actionType.js";
import ActiveChip from "../../components/activeChip.js";
import DatedAction from "../../components/datedAction.js";
import { getTenantConfigDetails } from "../../services/tenantService";
import getActionDefs from "./actionDefs";
import { PATHS } from "../../constants/paths.js";

const TenantForm = () => {
  const { id } = useParams();
  const [state, setState] = useState({
    data: {},
    errors: [],
    new: false,
  });
  // TODO: Set disableSkillsSync based on whether the sync process task is still running
  const [disableSkillsSync, setDisableSkillsSync] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      dispatch(layoutActions.changeBreadcrumbName(null));
      getTenant(id).then((res) => {
        setState((curState) => ({
          ...curState,
          data: res.data,
          new: false,
        }));
        dispatch(
          layoutActions.changeBreadcrumbName({
            breadcrumbName: res.data.name,
          })
        );
      });
      getTenantConfigDetails(id).then((res) => {
        setState((curState) => ({
          ...curState,
          data: { ...curState.data, ...res.data },
        }));
      });
    } else {
      setState((curState) => ({
        ...curState,
        new: true,
        data: { isActive: true },
      }));
    }
  }, []);

  const tenantHandler = async (actionType) => {
    if (
      state.errors.length > 0 &&
      (actionType === ACTION_TYPE.ADD || actionType === ACTION_TYPE.EDIT)
    ) {
      dispatch(
        toastActions.showErrorMessage(t("tenantsScreen.invalidFieldsMessage"))
      );
      return;
    }
    let res;
    let successMessage = "";
    switch (actionType) {
      case ACTION_TYPE.ADD:
        res = await addTenant(state.data);
        successMessage = t("tenantsScreen.messages.addSuccess");
        break;
      case ACTION_TYPE.EDIT:
        res = await editTenant(id, state.data);
        successMessage = t("tenantsScreen.messages.editSuccess");
        break;
      case ACTION_TYPE.DELETE:
        res = await deleteTenant(id);
        successMessage = t("tenantsScreen.messages.deleteSuccess", {
          name: state.data.name,
        });
        break;
      case ACTION_TYPE.RESTORE:
        res = await restoreTenant(id);
        successMessage = t("tenantsScreen.messages.restoreSuccess", {
          name: state.data.name,
        });
        break;
      default:
        break;
    }
    if (res.status === 200) {
      dispatch(toastActions.showSuccessMessage(successMessage));
    }
    navigate(PATHS.ADMIN_TENANTS_LANDING);
  };

  if (id && !Object.hasOwn(state.data, "userSubscriptionEnabled")) {
    return <SFSkeleton />;
  }

  const leaveHandler = () => {
    navigate(PATHS.ADMIN_TENANTS_LANDING);
  };

  const handleSyncSkillsAsync = async () => {
    await syncTenantSkills(id);
    setDisableSkillsSync(true);
    dispatch(
      toastActions.showInfoMessage(
        t("tenantsScreen.tenantsForm.syncSkillsStarted", {
          tenantName: state.data.name,
        })
      )
    );
  };

  const actionDefs = getActionDefs({
    t,
    onSyncSkillsClick: handleSyncSkillsAsync,
    disableSkillsSync,
    onCancelClick: leaveHandler,
    onTenantHandlerClick: () => tenantHandler(id ? ACTION_TYPE.EDIT : ACTION_TYPE.ADD),
    onDeleteClick: () => tenantHandler(ACTION_TYPE.DELETE),
    onRestoreClick: () => tenantHandler(ACTION_TYPE.RESTORE),
    stateProp: state,
    idProp: id,
  });


  return (
    <>
      <SFPageContainer disableGutters>
        {(Object.keys(state.data).length > 0) && (
          <SFActionBar
            titleProps={{
              title: t("adminRoutes.addTenant"),
            }}
            actionDefs={actionDefs}
          />
        )}
        <SFCard>
          <SFCardBody>
            <SFGridContainer spacing={2}>
              <SFGridItem xs={12}>
                <SFFormBuilder
                  schema={getSchema()}
                  uischema={uischema}
                  data={state.data}
                  onChange={(data, errors) =>
                    setState((curState) => ({ ...curState, data, errors }))
                  }
                />
              </SFGridItem>
              <SFGridItem xs={12}>
                <ActiveChip active={state.data.isActive} />
              </SFGridItem>
              <SFGridItem xs={12}>
                <SFDivider />
              </SFGridItem>
              <SFGridContainerItem>
                <SFGridItem xs={6}>
                  <DatedAction
                    user={state.data.createdBy}
                    date={state.data.createdAt}
                    label={t("tenantsScreen.tenantsForm.createdByLabel")}
                  />
                </SFGridItem>
                <SFGridItem xs={6}>
                  <DatedAction
                    user={state.data.updatedBy}
                    date={state.data.updatedAt}
                    label={t("tenantsScreen.tenantsForm.updatedByLabel")}
                  />
                </SFGridItem>
                <SFGridItem xs={6}>
                  <DatedAction
                    user={state.data.deletedBy}
                    date={state.data.deletedAt}
                    label={t("tenantsScreen.tenantsForm.deletedByLabel")}
                  />
                </SFGridItem>
              </SFGridContainerItem>
            </SFGridContainer>
          </SFCardBody>
        </SFCard>
      </SFPageContainer>
    </>
  );
};

export default TenantForm;
